import React, { useState } from "react";
import { motion } from "framer-motion";
import SectionTitle from "./SectionTitle";
import { contact, site } from "../content/home";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const inputBase =
  "block w-full rounded-xl border border-black/10 bg-white/60 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-brand-500/60 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-white/10 dark:bg-white/[0.03] dark:text-white dark:placeholder-white/30";

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const response = await fetch(site.contactPostUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error(contact.form.errorGeneric);
      setSubmissionSuccess(true);
      setFormData({ firstName: "", lastName: "", email: "", message: "" });
    } catch (err) {
      setSubmissionSuccess(false);
      setErrorMessage(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="iletisim" className="relative scroll-mt-24 py-24">
      <div className="mx-auto max-w-6xl container-px">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="section-eyebrow mb-4">
          <span className="h-px w-8 bg-brand-500" /> {contact.eyebrow}
        </motion.div>
        <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <SectionTitle lead={contact.title.lead} accent={contact.title.accent} tail={contact.title.tail} className="max-w-2xl" />
        </motion.div>

        <div className="mt-12 grid gap-8 lg:grid-cols-5">
          <motion.div variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={{ once: true }} className="lg:col-span-2 space-y-3">
            {contact.cards.map((c) => (
              <a key={c.kind} href={c.href} className="glass-card group flex items-center justify-between p-5 hover:border-black/20 dark:hover:border-white/20">
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-white/40">{c.label}</div>
                  <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">{c.value}</div>
                </div>
                <svg viewBox="0 0 512 512" className="h-5 w-5 -rotate-45 text-gray-400 transition-transform group-hover:rotate-0 group-hover:text-gray-900 dark:text-white/40 dark:group-hover:text-white" fill="currentColor">
                  <path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z" />
                </svg>
              </a>
            ))}
          </motion.div>

          <motion.form
            variants={fadeUp}
            custom={3}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="glass-card p-6 sm:p-8 lg:col-span-3"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs uppercase tracking-wider text-gray-500 dark:text-white/50">{contact.form.firstName.label}</label>
                <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder={contact.form.firstName.placeholder} className={inputBase} />
              </div>
              <div>
                <label className="mb-2 block text-xs uppercase tracking-wider text-gray-500 dark:text-white/50">{contact.form.lastName.label}</label>
                <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder={contact.form.lastName.placeholder} className={inputBase} />
              </div>
            </div>
            <div className="mt-4">
              <label className="mb-2 block text-xs uppercase tracking-wider text-gray-500 dark:text-white/50">{contact.form.email.label}</label>
              <input type="email" required name="email" value={formData.email} onChange={handleChange} placeholder={contact.form.email.placeholder} className={inputBase} />
            </div>
            <div className="mt-4">
              <label className="mb-2 block text-xs uppercase tracking-wider text-gray-500 dark:text-white/50">{contact.form.message.label}</label>
              <textarea
                name="message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                placeholder={contact.form.message.placeholder}
                className={inputBase}
              />
            </div>
            <div className="mt-6 flex items-center justify-between gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-brand disabled:opacity-50"
              >
                {isSubmitting ? contact.form.submitting : contact.form.submit}
                <svg viewBox="0 0 512 512" className="h-4 w-4" fill="currentColor">
                  <path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z" />
                </svg>
              </button>
              {submissionSuccess === true && (
                <span className="text-sm text-emerald-600 dark:text-emerald-400">{contact.form.success}</span>
              )}
              {submissionSuccess === false && (
                <span className="text-sm text-rose-600 dark:text-rose-400">{errorMessage}</span>
              )}
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;